/**
 * Легенда функционального зонирования.
 *
 * Строится из ZONE_GROUPS — из того же объекта, из которого собирается
 * выражение fill-color. Иначе цвет на карте и цвет в легенде однажды разойдутся,
 * и карта начнёт врать о содержании документа.
 */

import type { GenplanZone } from "~/config/genplanZones2024";
import { ZONE_GROUPS, UNKNOWN_ZONE_COLOR } from "~/config/zones";
import { el } from "./dom";

export interface LegendOptions {
  /** Коды зон, реально встретившиеся в загруженных тайлах. Пусто — показываем всё. */
  presentCodes?: ReadonlySet<string>;
  /** Легенды растровых листов: подпись слоя -> адрес картинки. */
  sheetLegends?: { title: string; url: string }[];
  /** Разобранные легенды листов: те же обозначения, но текстом и цветом. */
  sheetTaxonomies?: { title: string; zones: GenplanZone[] }[];
}

/**
 * Легенда листа, разобранная в таблицу.
 *
 * Лучше картинки, вырезанной из чертежа: читается на телефоне, ищется
 * браузером, доступна для чтения с экрана — и главное, это ровно те цвета, по
 * которым определяется зона при клике. Один источник на оба применения значит,
 * что легенда и ответ карты не могут разойтись.
 */
function taxonomyLegend(entry: { title: string; zones: GenplanZone[] }): HTMLElement {
  return el(
    "section",
    { class: "legend__group" },
    el("h3", { class: "legend__group-title", text: entry.title }),
    el(
      "ul",
      { class: "legend__list" },
      ...entry.zones.map((zone) =>
        el(
          "li",
          { class: "legend__item", attrs: { title: zone.description } },
          el("span", {
            class: "legend__swatch",
            attrs: {
              style: `background:rgb(${zone.rgb[0]},${zone.rgb[1]},${zone.rgb[2]})`,
              "aria-hidden": "true",
            },
          }),
          el(
            "span",
            { class: "legend__labels" },
            el("span", { class: "legend__title", text: zone.title }),
          ),
        ),
      ),
    ),
    el("p", {
      class: "legend__note",
      text: "Обозначения и цвета — с легенды самого листа генплана. Планируемые зоны "
        + "нарисованы на чертеже той же краской со штриховкой, поэтому по цвету их "
        + "от существующих не отличить.",
    }),
  );
}

/**
 * Легенда растрового листа — картинка, вырезанная из самого чертежа.
 *
 * Построить её из данных невозможно: в растре нет ни кодов зон, ни атрибутов.
 * Зато на листе она напечатана целиком и официально, поэтому показывается
 * как есть — это и есть первоисточник.
 *
 * Картинка обязана открываться в полный размер. В боковой панели ширина около
 * трёхсот пикселей, а легенда листа — полторы тысячи и до шести тысяч в высоту:
 * ужатая до панели, она превращается в неразборчивую полосу. Показывать
 * обозначения так, что прочитать их нельзя, — всё равно что не показывать.
 */
function sheetLegend(entry: { title: string; url: string }): HTMLElement {
  const image = el("img", {
    class: "legend__sheet",
    attrs: {
      src: entry.url,
      alt: `Условные обозначения листа «${entry.title}»`,
      loading: "lazy",
    },
  });

  return el(
    "section",
    { class: "legend__group" },
    el("h3", { class: "legend__group-title", text: entry.title }),
    // Обычная ссылка, а не своё окно просмотра: встроенный просмотрщик
    // картинок в браузере уже умеет масштаб, перетаскивание и сохранение, и на
    // телефоне работает привычнее любого самодельного.
    el(
      "a",
      {
        class: "legend__sheet-link",
        attrs: {
          href: entry.url,
          target: "_blank",
          rel: "noopener",
          title: "Открыть обозначения в полный размер",
        },
      },
      image,
      el("span", { class: "legend__zoom", text: "Открыть в полный размер" }),
    ),
    el("p", {
      class: "legend__note",
      text: "Обозначения приведены по самому листу генплана.",
    }),
  );
}

export function createLegend(options: LegendOptions = {}): HTMLElement {
  const { presentCodes, sheetLegends = [], sheetTaxonomies = [] } = options;

  // Если опубликованы только растровые листы, палитра кодов не имеет смысла:
  // показываем обозначения самого чертежа и ничего не выдумываем. Разобранная
  // в таблицу легенда предпочтительнее вырезанной картинки, поэтому идёт первой.
  // Ни один лист не включён — сказать об этом прямо. Пустая вкладка выглядит
  // так, будто обозначений нет вовсе.
  if (sheetTaxonomies.length === 0 && sheetLegends.length === 0 && !presentCodes) {
    return el(
      "div",
      { class: "legend", attrs: { role: "region", "aria-label": "Условные обозначения" } },
      el("p", {
        class: "legend__note",
        text: "Включите слой генплана во вкладке «Слои» — здесь появятся его условные обозначения.",
      }),
    );
  }

  if (sheetTaxonomies.length > 0 || sheetLegends.length > 0) {
    return el(
      "div",
      {
        class: "legend",
        attrs: { role: "region", "aria-label": "Условные обозначения генплана" },
      },
      ...sheetTaxonomies.map(taxonomyLegend),
      ...sheetLegends.map(sheetLegend),
    );
  }
  const filtered = presentCodes && presentCodes.size > 0;

  const groups = ZONE_GROUPS.map((group) => {
    const zones = filtered
      ? group.zones.filter((zone) => presentCodes.has(zone.code))
      : group.zones;
    if (zones.length === 0) return null;

    return el(
      "section",
      { class: "legend__group" },
      el("h3", { class: "legend__group-title", text: group.title }),
      el(
        "ul",
        { class: "legend__list" },
        ...zones.map((zone) =>
          el(
            "li",
            { class: "legend__item", attrs: { title: zone.description ?? zone.title } },
            el("span", {
              class: "legend__swatch",
              attrs: { style: `background:${zone.color}`, "aria-hidden": "true" },
            }),
            el(
              "span",
              { class: "legend__labels" },
              el("span", { class: "legend__code", text: zone.code }),
              el("span", { class: "legend__title", text: zone.title }),
            ),
          ),
        ),
      ),
    );
  }).filter((node): node is HTMLElement => node !== null);

  return el(
    "div",
    { class: "legend", attrs: { role: "region", "aria-label": "Легенда функционального зонирования" } },
    ...groups,
    el(
      "p",
      { class: "legend__note" },
      el("span", {
        class: "legend__swatch",
        attrs: { style: `background:${UNKNOWN_ZONE_COLOR}`, "aria-hidden": "true" },
      }),
      "Код зоны отсутствует в палитре — проверьте исходные данные",
    ),
  );
}
